import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/auth';
import { messages } from '@/i18n';
import { AuthScreenLayout } from './AuthScreenLayout';

const INPUT_CLASS =
  'border-border bg-secondary/40 focus-visible:ring-ring h-9 rounded-md border px-3 text-sm outline-none focus-visible:ring-2';

export function ForgotPassword() {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setPending(true);
    // Always render the same confirmation, whatever happens: Firebase's own
    // email-enumeration protection means a failed lookup reveals nothing
    // useful, and a UI that distinguished "no such account" would reopen the
    // oracle the platform just closed.
    // .catch() before .finally(), not instead of it: .finally() re-throws, so
    // without this the ignored failure is an unhandled rejection rather than a
    // deliberately swallowed one.
    void resetPassword(email)
      .catch(() => {
        // Ignored on purpose. See above.
      })
      .finally(() => {
        setPending(false);
        setSent(true);
      });
  };

  if (sent) {
    return (
      <AuthScreenLayout title={messages.forgotPassword.title}>
        <Alert>
          <AlertDescription>
            <p>{messages.forgotPassword.confirmation}</p>
            <p className="mt-2">{messages.forgotPassword.checkSpam}</p>
          </AlertDescription>
        </Alert>
        <Button asChild variant="outline" className="mt-4 w-full">
          <Link to="/sign-in">{messages.common.backToSignIn}</Link>
        </Button>
      </AuthScreenLayout>
    );
  }

  return (
    <AuthScreenLayout
      title={messages.forgotPassword.title}
      description={messages.forgotPassword.body}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        <label className="flex flex-col gap-1 text-sm">
          {messages.signIn.emailLabel}
          <input
            type="email"
            required
            autoComplete="username"
            className={INPUT_CLASS}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={pending}
          />
        </label>

        <Button type="submit" className="w-full" disabled={pending}>
          <KeyRound aria-hidden />
          {messages.forgotPassword.submit}
        </Button>

        <Link to="/sign-in" className="text-muted-foreground text-center text-xs hover:underline">
          {messages.common.backToSignIn}
        </Link>
      </form>
    </AuthScreenLayout>
  );
}
